import CustomizeControlsTable from "@/modules/agent/components/customize-controls/CustomizeControlsTable";
import { CustomizeControlsProvider } from "@/modules/agent/components/customize-controls/CustomizeControls";
import { useSearchParams, Navigate, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { getCustomizeControlsActions, useCustomizeControlsStore } from "@/modules/agent/states/cusomize-controls";
import ErrorBoundary from "@/shared/components/error-boundary";
import { ZButton } from "@/shared/components/button";
import ZBreadCrumbs from "@/shared/components/custom/breadcrumbs";
import { addNotification } from "@/shared/states/notification";
import parsePromise from "@/shared/utils/parsePromise";
import { updateControlSetControls } from "@/modules/settings/requests";
import { ROUTES, SETTINGS_ROUTES } from "@/shared/constants/routes";
import { uploadFileReq } from "@/shared/requests/upload-file";
import { checkAndRefreshUrl } from "@/modules/agent/utils/refreshUrl";
import ControlSetHeader from "./ControlSetHeader";
import { AgentSessionType } from "@/modules/sessions/types";
import { VendorAssessmentControl } from "@/modules/agent/states/cusomize-controls";
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter } from "@nextui-org/react";
import { post } from "@/infra/rest";

const ControlSetEditPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isSaving, setIsSaving] = useState(false);
  const [isExitModalOpen, setIsExitModalOpen] = useState(false);
  
  // Get control set edit context and state from store
  const { controlSetEditContext, changesMade, dataState } = useCustomizeControlsStore();
  const isControlSetEdit = controlSetEditContext?.isControlSetEdit || false;
  const controlSetId = controlSetEditContext?.controlSetId;
  const controlsUrl = controlSetEditContext?.controlsUrl;
  const isNewControlSet = controlSetEditContext?.isNewControlSet || false;
  const controlSetName = controlSetEditContext?.controlSetName || 'Control Set';
  const importedControlsUrl = controlSetEditContext?.importedControlsUrl;
  
  
  // No cleanup effect - let the context persist until user explicitly exits

  // Save handler
  const handleSave = async () => {
    setIsSaving(true);
    try {
      if (!controlsUrl && !isNewControlSet) {
        throw new Error('No controls URL found');
      }

      // This condition should not occur anymore since Proceed button creates control set
      // But keeping as fallback for any edge cases
      if (isNewControlSet) {
        addNotification({
          type: "error",
          title: "Save Failed", 
          message: "Control set not properly initialized. Please try creating again.",
        });
        setIsSaving(false);
        return;
      }

      // Get current data directly from store (includes user edits)
      const currentData = useCustomizeControlsStore.getState().dataState;
      
      // Filter and transform data for save (inline transformation to avoid issues)
      const controlsToSave = currentData
        .filter(control => {
          // Filter out empty controls - for vendor assessment check category and question
          if (control.agentType === AgentSessionType.MULTI_FILE_VENDOR_ASSESSMENT) {
            const vendorControl = control as VendorAssessmentControl;
            return vendorControl.category && vendorControl.category.trim().length > 0 &&
                   vendorControl.question && vendorControl.question.trim().length > 0;
          }
          return false; // Only handle vendor assessment for now
        })
        .map(control => {
          // Transform data structure for vendor assessment
          if (control.agentType === AgentSessionType.MULTI_FILE_VENDOR_ASSESSMENT) {
            const vendorControl = control as VendorAssessmentControl;
            return {
              ...vendorControl,
              key: vendorControl.category, // Map category to key for backend
              category: undefined,         // Remove category field
              new_question: undefined,     // Remove temporary fields
              id: undefined,              // Remove temporary id
              selected: true              // Add selected flag
            };
          }
          return control;
        });


      // Check if there are any valid controls to save
      if (controlsToSave.length === 0) {
        addNotification({
          type: "warn", 
          message: "Please add at least one control before saving.",
        });
        setIsSaving(false);
        return;
      }
      
      const transformedControlsJson = JSON.stringify(controlsToSave, null, 2);
      
      
      const blob = new Blob([transformedControlsJson], { type: "application/json" });

      // Upload blob and update control set
      const validSignedUrl = await checkAndRefreshUrl(controlsUrl!);
      await uploadFileReq(validSignedUrl, blob);

      const [error] = await parsePromise(
        updateControlSetControls(controlSetId!, {
          controls_url: controlsUrl!,
          operation_type: "update_control_set_controls",
        })
      );

      if (error) throw error;

      addNotification({
        type: "success",
        message: "Controls saved successfully.",
      });
      
      // Navigate back to control sets list (same pattern as questionnaire)
      navigate(`/${ROUTES.SETTINGS}/${SETTINGS_ROUTES.THIRD_PARTY_CONTROL_SETS}`);
    } catch (error) {
      console.error('Error saving controls:', error);
      addNotification({
        type: "error",
        message: "An unexpected error occurred. Please try again later.",
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Check if there are unsaved changes that warrant showing the exit confirmation
  const hasUnsavedChanges = () => {
    // If user made any tracked changes (manual edits, AI generations, etc.)
    if (changesMade > 0) return true;
    
    // Check if there are any controls with actual content
    if (dataState.length > 0) {
      const hasValidControls = dataState.some(control => {
        // For vendor assessment controls (control sets)
        if (control.agentType === AgentSessionType.MULTI_FILE_VENDOR_ASSESSMENT) {
          const vendorControl = control as VendorAssessmentControl;
          // Check if control has meaningful content (not just empty placeholders)
          const hasCategory = vendorControl.category && vendorControl.category.trim().length > 0;
          const hasQuestion = vendorControl.question && vendorControl.question.trim().length > 0;
          return hasCategory && hasQuestion;
        }
        
        // Handle other agent types if needed in the future
        return false;
      });
      return hasValidControls;
    }
    
    return false;
  };

  const handleExit = () => {
    if (hasUnsavedChanges()) {
      // Show confirmation modal when there are unsaved changes
      setIsExitModalOpen(true);
    } else {
      // Direct exit when no changes were made
      const { setControlSetEditContext } = getCustomizeControlsActions();
      setControlSetEditContext(null);
      navigate('/settings');
    }
  };

  const handleExitWithoutSaving = () => {
    // Clear control set edit context before navigating
    const { setControlSetEditContext } = getCustomizeControlsActions();
    setControlSetEditContext(null);
    setIsExitModalOpen(false);
    navigate('/settings');
  };

  const handleSaveAndExit = async () => {
    setIsExitModalOpen(false);
    // Reuse the existing save function (which handles navigation)
    await handleSave();
  };
  
  // Redirect if missing required control set edit context
  // Allow new control sets without controlsUrl
  if (!isControlSetEdit || !controlSetId || (!controlsUrl && !isNewControlSet)) {
    return <Navigate to="/settings" replace />;
  }
  
  // Render with custom header that matches Image 2 exactly
  return (
    <ErrorBoundary fallback={null}>
      <CustomizeControlsProvider>
        <div className="w-full h-full flex flex-col">
          {/* Custom header with breadcrumb and Save/Exit buttons - matches Image 2 */}
          <div className="flex items-center pb-4 justify-between min-h-[44px] max-h-[44px]">
            <div className="flex gap-4 items-center">
              <ZBreadCrumbs 
                items={[
                  { title: controlSetName, href: "" },
                  { title: "Edit Controls", href: "" }
                ]} 
              />
            </div>
            <div className="flex gap-2">
              <ZButton
                variant="bordered"
                className="text-xs font-medium h-7 px-3"
                onClick={handleExit}
                isDisabled={isSaving}
              >
                Exit
              </ZButton>
              <ZButton
                color="primary"
                className="text-xs font-medium h-7 px-3"
                onClick={handleSave}
                isLoading={isSaving}
                isDisabled={isSaving}
              >
                Done
              </ZButton>
            </div>
          </div>
          
          {/* Table with same container styling */}
          <div className="grow overflow-hidden rounded-xl">
            <div className="h-full w-full overflow-hidden rounded-lg flex flex-col">
              <ControlSetHeader />
              <CustomizeControlsTable />
            </div>
          </div>
        </div>

        {/* Exit Confirmation Modal */}
        <Modal 
          isOpen={isExitModalOpen} 
          onClose={() => setIsExitModalOpen(false)}
          size="md"
          classNames={{
            wrapper: "z-[9999]",
            backdrop: "bg-black/50"
          }}
        >
          <ModalContent className="py-4">
            <ModalHeader className="flex flex-col gap-1 px-6 pb-2 items-center">
              <h3 className="text-lg font-semibold text-gray-900 ">
                Exit without saving controls?
              </h3>
            </ModalHeader>
            <ModalBody className="px-6 py-2 items-center">
              <p className="text-sm text-gray-600">
                All new edits to this control set will be lost.
              </p>
            </ModalBody>
            <ModalFooter className="px-6 pt-4">
              <ZButton
                variant="bordered"
                onClick={handleExitWithoutSaving}
                className="flex-1"
                isDisabled={isSaving}
              >
                Exit without Saving
              </ZButton>
              <ZButton
                color="primary"
                onClick={handleSaveAndExit}
                className="flex-1"
                isLoading={isSaving}
                isDisabled={isSaving}
              >
                Save & Exit
              </ZButton>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </CustomizeControlsProvider>
    </ErrorBoundary>
  );
};

export default ControlSetEditPage;